/**
 * Local prediction for the local player (DESIGN 5.6):
 *
 * Movement: every committed input frame steps `stepMovement` with the same
 * arena the server uses and is kept by `seq`. On a snapshot the server's
 * pose for `ack` replaces ours, inputs after `ack` are replayed, and the
 * visual position blends toward the corrected one over
 * CLIENT.correctionBlendMs (snapping when the error exceeds
 * CLIENT.snapErrorUnits).
 *
 * Queue: element presses run `pushElement` immediately (with the last known
 * wet flag) so the HUD reacts on the keypress. Casts, clears and matched
 * magicks consume the queue the way the server will: a Magick the server
 * would refuse (cast lock, frozen, shocked, stopped in time, dead, or the PvP
 * cooldown from the snapshot's `mc`) keeps it, as the server does. The server's `q` is
 * authoritative: on a snapshot it is compared with what we had predicted
 * *as of `ack`* (not the current queue, which may hold newer presses); a
 * mismatch replaces the queue, replays the unacknowledged actions on top,
 * and reports `fizzle` when the prediction had more elements.
 *
 * Weaving and the stash (docs/spells-depth.md): a stash tap runs `tapStash` at
 * once, so pieces move between the queue and the pods on the keypress. A
 * weave's result only arrives from the server when the weave finishes; until
 * then element presses are ignored (the server ignores them too), and a
 * server queue that differs from ours by exactly that weave, or by a Grand
 * Magick spending its pieces, counts as a match. The stash is compared and
 * replayed with the queue as one state.
 *
 * The rules-engine functions are injected so this module can be unit
 * tested while WP1/WP2 land their implementations.
 */

import type {
  Action,
  Arena,
  BaseElement,
  Collider,
  Element,
  MagickId,
  MoveInput,
  MoveResult,
  PlayerSnap,
  PushResult,
  StashSlot,
  Vec2,
} from '@shared/index.js';
import {
  ARENA, CAST_LOCK, CLIENT, LESSER_STOP, PLAYER_SPEED, PVP, STASH_SLOTS, applyWeave, matchGrand, pvpMagickCooldown, tapStash, weaveSeconds, weaveTarget,
} from '@shared/index.js';
import type { QueueFeedbackOutcome } from './viewModel.js';

export type StepFn = (
  pos: Vec2,
  vel: Vec2,
  mv: MoveInput,
  speed: number,
  dt: number,
  arena: Arena,
  extraCircles?: readonly Collider[],
) => MoveResult;
export type PushFn = (queue: readonly Element[], x: BaseElement, casterWet: boolean) => PushResult;
export type MatchMagickFn = (queue: readonly Element[]) => MagickId | null;

export interface PredictionDeps {
  step: StepFn;
  push: PushFn;
  matchMagick: MatchMagickFn;
}

/** One input frame as sent to the server, plus the queue predicted after its actions. */
export interface CommittedInput {
  seq: number;
  mv: MoveInput;
  /** Seconds this input covers (time since the previous flush). */
  dt: number;
  acts: Action[];
  queueAfter: Element[];
  /** The stash predicted after its actions. */
  stashAfter: StashSlot[];
}

/** What a local action did to the predicted queue, for HUD and SFX feedback. */
export interface QueueChange {
  outcome: QueueFeedbackOutcome;
  /** The pressed element, or the fused result for combined/degraded. */
  el: Element | null;
}

export type QueueReconcile = 'match' | 'replaced' | 'fizzle';

/**
 * What the server will do with a Magick press (players.ts canCastMagick, spells/caster.ts
 * castMagick): cast it, refuse it because the PvP cooldown runs, refuse it for another
 * reason (cast lock, frozen or prone, shocked, stopped in time, dead), or ignore it (no Magick matches).
 * Only `cast` consumes the queue.
 */
export type MagickVerdict = 'cast' | 'cooldown' | 'blocked' | 'none';

/** The PvP Magick cooldown as the HUD shows it: seconds left and the length it started at. */
export interface MagickCooldown { left: number; total: number }

/**
 * Unacknowledged inputs kept for replay: 10 s at NET.clientInputHz. Nothing
 * acknowledges inputs while the socket is down, or ever again after it closed
 * for good, and the frame loop keeps committing at 20 Hz, so without a cap a
 * tab left open that way grew this list (and the replay on the next snapshot)
 * by 72,000 entries an hour. Past this many, the oldest is treated as applied.
 */
export const MAX_PENDING_INPUTS = 200;

/** A Lesser Time Stop we cast this long ago that the server hasn't confirmed yet still counts as cooling down. */
const LESSER_UNCONFIRMED_MS = 1500;

export function sameQueue(a: readonly Element[], b: readonly Element[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

export class Prediction {
  /** Predicted position after every committed input (not the blended visual one). */
  pos: Vec2;
  vel: Vec2 = { x: 0, y: 0 };
  /** `sm` from the last snapshot: PLAYER_SPEED multiplier for statuses and channels. */
  speedMul = 1;
  /** Dead players are not predicted; the server pose is shown as-is. */
  dead = false;
  /** Last known wet flag (DESIGN 5.6 "using its last known wet status"). */
  wet = false;
  /** The predicted queue. */
  queue: Element[] = [];
  /** The predicted stash (docs/spells-depth.md). */
  stash: StashSlot[] = emptyStash();
  /** A weave or incantation runs on the server (its `wv` / `gi`), so element presses do nothing. */
  private busy = false;
  /** Local ms until which a weave we just started keeps the hands busy. */
  private weaveUntil = Number.NEGATIVE_INFINITY;
  /** A PvP room: Magicks share one cooldown per player (pvpMagickCooldown). Set per room. */
  pvp = false;
  /** Someone on our team is down, so a Revive has a target and spends the PvP cooldown. Set per snapshot. */
  reviveTarget = false;
  /** Frozen or knocked down (only Teleport casts) and shocked (no Magick), from the last snapshot. */
  private rooted = false;
  private shocked = false;
  /** Time Stop or a slowing field has our time under half speed (depth.ts stoppedInTime): no cast, Magick or weave starts. */
  private stopped = false;
  /** Presses `refuses` turned away at keypress time: a replay must not bring them back. */
  private readonly refused = new WeakSet<Action>();

  private pending: CommittedInput[] = [];
  /** Actions applied to the queue since the last commit (not yet assigned a seq). */
  private unflushed: Action[] = [];
  /** The last server-confirmed queue: what the server had when nothing of ours was pending. */
  private baseQueue: Element[] = [];
  private baseStash: StashSlot[] = emptyStash();
  /** Visual correction: shown position = predicted + offset * (1 - age / blendMs). */
  private offset: Vec2 = { x: 0, y: 0 };
  private offsetAt = Number.NEGATIVE_INFINITY;
  /** Local ms until which a predicted cast lock (CAST_LOCK) blocks another cast. */
  private castLockUntil = Number.NEGATIVE_INFINITY;
  /** Local ms when the PvP Magick cooldown ends: the server's `mc`, or a Magick we cast that it has not seen yet. */
  private magickReadyAt = Number.NEGATIVE_INFINITY;
  /** Co-op: local ms when the Lesser Time Stop's own cooldown ends. */
  private lesserReadyAt = Number.NEGATIVE_INFINITY;
  /** Length in seconds of the cooldown that ends at magickReadyAt, for the HUD's ring. */
  private magickCdTotal = 0;
  /** Magick presses predicted to cast, and the cooldown end each one started (replays and reconcile read it). */
  private readonly castMagicks = new WeakMap<Action, number>();

  constructor(private readonly deps: PredictionDeps, private arena: Arena = ARENA) {
    this.pos = { x: arena.spawn.x, y: arena.spawn.y };
  }

  /** The room plays another map (the `room` frame's level): predict against its walls from now on. */
  setArena(arena: Arena): void {
    this.arena = arena;
  }

  /** Inputs committed but not yet acknowledged by a snapshot. */
  get pendingInputs(): readonly CommittedInput[] { return this.pending; }

  /** Forget everything (room change / reconnect to a new room). */
  reset(pos: Vec2 = this.arena.spawn): void {
    this.pos = { x: pos.x, y: pos.y };
    this.vel = { x: 0, y: 0 };
    this.speedMul = 1;
    this.dead = false;
    this.wet = false;
    this.queue = [];
    this.stash = emptyStash();
    this.busy = false;
    this.weaveUntil = Number.NEGATIVE_INFINITY;
    this.pending = [];
    this.unflushed = [];
    this.baseQueue = [];
    this.baseStash = emptyStash();
    this.offset = { x: 0, y: 0 };
    this.offsetAt = Number.NEGATIVE_INFINITY;
    this.castLockUntil = Number.NEGATIVE_INFINITY;
    this.reviveTarget = false;
    this.rooted = false;
    this.shocked = false;
    this.stopped = false;
    this.magickReadyAt = Number.NEGATIVE_INFINITY;
    this.magickCdTotal = 0;
  }

  /** The PvP Magick cooldown at local time `now`, or null when a Magick could go. */
  magickCooldown(now: number): MagickCooldown | null {
    const left = (this.magickReadyAt - now) / 1000;
    if (!this.pvp || !(left > 0)) return null;
    return { left, total: Math.max(left, this.magickCdTotal) };
  }

  /** What the server will do with a Magick press now, given the queue we predict (see MagickVerdict). */
  magickVerdict(now: number): MagickVerdict {
    const id = this.queue.length > 0 ? this.deps.matchMagick(this.queue) : null;
    if (id === null) return 'none';
    if (this.dead || now < this.castLockUntil || this.shocked || this.stopped || this.handsBusy(now)) return 'blocked';
    if (this.rooted && id !== 'teleport') return 'blocked';
    if (this.pvp && now < this.magickReadyAt) return 'cooldown';
    // co-op: the Lesser Time Stop has a cooldown of its own (weaves.ts LESSER_STOP)
    if (!this.pvp && id === 'stoptime' && now < this.lesserReadyAt) return 'cooldown';
    return 'cast';
  }

  /** Would a Force press cast now (players.ts canCast): a queue, no cast lock, not dead, frozen, prone, shocked, stopped in time or weaving. */
  canForce(now: number): boolean {
    return this.queue.length > 0 && now >= this.castLockUntil && !this.dead && !this.rooted && !this.shocked && !this.stopped && !this.handsBusy(now);
  }

  /**
   * Apply an action to the predicted queue right away (keypress time) and
   * remember it for the next commit. Returns feedback for element presses
   * (null for self_shock, which is only trusted from the server, DESIGN 5.6).
   */
  act(a: Action, now: number): QueueChange | null {
    this.unflushed.push(a);
    return this.applyToQueue(a, now, true);
  }

  /**
   * Commit one input frame: the unflushed actions (at most `maxActs`; the
   * rest wait for the next frame so the server never rejects an oversized
   * input) get `seq`, and movement advances by `dt` seconds under `mv`.
   * Returns the actions to send.
   */
  commit(seq: number, mv: MoveInput, dt: number, maxActs: number = Number.POSITIVE_INFINITY): Action[] {
    let acts: Action[];
    if (this.unflushed.length > maxActs) {
      acts = this.unflushed.slice(0, maxActs);
      this.unflushed = this.unflushed.slice(maxActs);
    } else {
      acts = this.unflushed;
      this.unflushed = [];
    }
    if (!this.dead && dt > 0) {
      const r = this.deps.step(this.pos, this.vel, mv, this.speed(), dt, this.arena);
      this.pos = r.pos;
      this.vel = r.vel;
    }
    this.pending.push({ seq, mv, dt, acts, queueAfter: this.queue, stashAfter: this.stash });
    if (this.pending.length > MAX_PENDING_INPUTS) {
      // The dropped inputs count as applied: their queue becomes the confirmed base, and the
      // movement replay starts after them (a server that far behind gets a snap, not a blend).
      const dropped = this.pending.splice(0, this.pending.length - MAX_PENDING_INPUTS);
      this.baseQueue = dropped[dropped.length - 1]!.queueAfter;
      this.baseStash = dropped[dropped.length - 1]!.stashAfter;
    }
    return acts;
  }

  /**
   * Reconcile with the server's view of us (DESIGN 5.6). `ack` is the last
   * input seq the server applied before building this snapshot; `now` is
   * local ms for the correction blend.
   */
  reconcile(p: PlayerSnap, ack: number, now: number): QueueReconcile {
    const visual = this.renderPos(now, null, 0);
    this.speedMul = p.sm;
    this.dead = p.dead === 1;
    this.wet = p.st.some((s) => s.id === 'wet');
    this.rooted = p.st.some((s) => s.id === 'frozen' || s.id === 'knockdown');
    this.shocked = p.st.some((s) => s.id === 'shocked');
    this.stopped = (p.ts ?? 1) < 0.5;
    this.busy = p.wv !== undefined || p.gi !== undefined;
    const serverStash = stashOf(p.sx);

    // --- queue and stash: compare against the prediction as of ack, then replay what the server has not seen.
    let atAck: readonly Element[] = this.baseQueue;
    let atAckStash: readonly StashSlot[] = this.baseStash;
    const remaining: CommittedInput[] = [];
    for (const i of this.pending) {
      if (i.seq <= ack) { atAck = i.queueAfter; atAckStash = i.stashAfter; }
      else remaining.push(i);
    }
    // The server has answered every weave press: its `wv` is the truth now (it refused the press, or a hit broke the weave).
    if (!this.busy && !remaining.some((i) => i.acts.some(isWeave)) && !this.unflushed.some(isWeave)) this.weaveUntil = Number.NEGATIVE_INFINITY;
    let result: QueueReconcile = 'match';
    if (!sameQueue(atAck, p.q) || !sameStash(atAckStash, serverStash)) {
      // A finished weave or a Grand Magick spending its pieces is the server running ahead, not a misprediction.
      if (!explained(atAck, atAckStash, p.q, serverStash)) result = atAck.length > p.q.length ? 'fizzle' : 'replaced';
      this.queue = p.q.slice();
      this.stash = serverStash.slice();
      for (const i of remaining) {
        for (const a of i.acts) this.applyToQueue(a, now, false);
        i.queueAfter = this.queue;
        i.stashAfter = this.stash;
      }
      for (const a of this.unflushed) this.applyToQueue(a, now, false);
    }
    this.baseQueue = p.q.slice();
    this.baseStash = serverStash.slice();
    this.pending = remaining;

    // --- Magick cooldown: the server's as of ack, unless a Magick it has not seen yet started a later one.
    let ready = p.mc ? now + p.mc * 1000 : Number.NEGATIVE_INFINITY;
    if (p.mc && !(this.magickReadyAt > now)) this.magickCdTotal = p.mc > PVP.quickCooldown ? PVP.magickCooldown : PVP.quickCooldown;
    for (const i of remaining) for (const a of i.acts) ready = Math.max(ready, this.castMagicks.get(a) ?? ready);
    for (const a of this.unflushed) ready = Math.max(ready, this.castMagicks.get(a) ?? ready);
    this.magickReadyAt = ready;
    // co-op Lesser Time Stop: the server's cooldown, or our own cast it hasn't confirmed yet
    if (p.lc) this.lesserReadyAt = now + p.lc * 1000;
    else if (!(this.lesserReadyAt - now > LESSER_STOP.cooldown * 1000 - LESSER_UNCONFIRMED_MS)) this.lesserReadyAt = Number.NEGATIVE_INFINITY;

    // --- movement: adopt the server pose for ack and replay the rest.
    this.pos = { x: p.x, y: p.y };
    this.vel = { x: p.vx, y: p.vy };
    if (!this.dead) {
      for (const i of remaining) {
        if (i.dt <= 0) continue;
        const r = this.deps.step(this.pos, this.vel, i.mv, this.speed(), i.dt, this.arena);
        this.pos = r.pos;
        this.vel = r.vel;
      }
    }
    const ex = visual.x - this.pos.x, ey = visual.y - this.pos.y;
    if (this.dead || Math.hypot(ex, ey) > CLIENT.snapErrorUnits) {
      this.offset = { x: 0, y: 0 };
      this.offsetAt = Number.NEGATIVE_INFINITY;
    } else {
      this.offset = { x: ex, y: ey };
      this.offsetAt = now;
    }
    return result;
  }

  /**
   * The position to draw: the committed prediction, advanced by a preview
   * sub-step of `previewDt` seconds under `mv` (so motion stays smooth
   * between 20 Hz commits without committing anything), plus the decaying
   * correction offset.
   */
  renderPos(now: number, mv: MoveInput | null, previewDt: number): Vec2 {
    let p = this.pos;
    if (mv && previewDt > 0 && !this.dead) {
      p = this.deps.step(this.pos, this.vel, mv, this.speed(), previewDt, this.arena).pos;
    }
    const k = this.blendFactor(now);
    return { x: p.x + this.offset.x * k, y: p.y + this.offset.y * k };
  }

  /** 1 right after a correction, 0 once CLIENT.correctionBlendMs have passed. */
  blendFactor(now: number): number {
    const age = now - this.offsetAt;
    if (!(age >= 0) || age >= CLIENT.correctionBlendMs) return 0;
    return 1 - age / CLIENT.correctionBlendMs;
  }

  private speed(): number {
    return PLAYER_SPEED * this.speedMul;
  }

  /**
   * What the server will do to the queue for one action. `live` actions
   * honour the predicted cast lock; replays do not (the lock state at that
   * time is unknown, and the server's answer arrives with the next ack).
   */
  private applyToQueue(a: Action, now: number, live: boolean): QueueChange | null {
    if (live && this.refuses(a, now)) this.refused.add(a);
    if (this.refused.has(a)) return null;
    switch (a.a) {
      case 'weave': {
        const r = weaveTarget(this.queue);
        if (live && r) this.weaveUntil = now + weaveSeconds(r) * 1000;
        return null;
      }
      case 'stash': {
        const r = tapStash(this.queue, this.stash, a.s);
        this.queue = r.queue;
        this.stash = r.stash;
        return null;
      }
      case 'el': {
        const r = this.deps.push(this.queue, a.e, this.wet);
        this.queue = r.queue;
        if (r.outcome === 'self_shock') return null;
        const fused = (r.outcome === 'combined' || r.outcome === 'degraded') ? r.queue[r.index] : undefined;
        return { outcome: r.outcome, el: fused ?? a.e };
      }
      case 'clear':
        this.queue = [];
        return null;
      case 'cast':
        // Every mode consumes the queue on begin (DESIGN 2.4: force starts the
        // channel/charge, area/self fire, weapon imbues). `end` only releases.
        if (a.ph === 'b' && this.queue.length > 0 && (!live || now >= this.castLockUntil)) {
          this.queue = [];
          if (live) this.castLockUntil = now + CAST_LOCK * 1000;
        }
        return null;
      case 'magick': {
        // A Grand Magick incants first; its pieces go when the server says it happened.
        if (matchGrand(this.queue, this.stash)) return null;
        if (!live) {
          // A replay does what the keypress predicted: the gate's state back then is not known now.
          if (this.castMagicks.has(a) && this.queue.length > 0 && this.deps.matchMagick(this.queue) !== null) this.queue = [];
          return null;
        }
        const verdict = this.magickVerdict(now);
        if (verdict === 'cooldown') return { outcome: 'magick_wait', el: null };
        if (verdict !== 'cast') return null;
        const id = this.deps.matchMagick(this.queue)!;
        this.queue = [];
        this.castLockUntil = now + CAST_LOCK * 1000;
        if (this.pvp && (id !== 'revive' || this.reviveTarget)) {
          this.magickCdTotal = pvpMagickCooldown(id);
          this.magickReadyAt = now + this.magickCdTotal * 1000;
        }
        if (!this.pvp && id === 'stoptime') this.lesserReadyAt = now + LESSER_STOP.cooldown * 1000;
        this.castMagicks.set(a, this.magickReadyAt);
        return null;
      }
      case 'melee':
        return null;
    }
  }

  /** A weave we started, or one the server reports, keeps the hands busy. */
  private handsBusy(now: number): boolean {
    return this.busy || now < this.weaveUntil;
  }

  /**
   * The server's gates as far as the last snapshot shows them (players.ts applyPlayerInput and canCast, depth.ts
   * startWeave and tapStashSlot): a press it will refuse leaves the queue and stash alone, so they don't blink. Stopped
   * in time, the stash still works: only tapStashSlot leaves that gate out. The cast lock isn't here: replays never
   * honoured it.
   */
  private refuses(a: Action, now: number): boolean {
    const busy = this.handsBusy(now);
    switch (a.a) {
      case 'el': return busy;
      case 'weave': return this.dead || this.rooted || this.shocked || this.stopped || busy;
      case 'stash': return this.dead || this.rooted || this.shocked || busy;
      case 'cast': return a.ph === 'b' && (this.dead || this.shocked || this.stopped || busy || (this.rooted && a.m !== 'self'));
      default: return false;
    }
  }
}

function isWeave(a: Action): boolean {
  return a.a === 'weave';
}

function emptyStash(): StashSlot[] {
  return new Array<StashSlot>(STASH_SLOTS).fill(null);
}

function stashOf(sx: readonly StashSlot[] | undefined): StashSlot[] {
  const s = emptyStash();
  if (sx) for (let i = 0; i < s.length; i++) s[i] = sx[i] ?? null;
  return s;
}

function sameStash(a: readonly StashSlot[], b: readonly StashSlot[]): boolean {
  for (let i = 0; i < STASH_SLOTS; i++) if ((a[i] ?? null) !== (b[i] ?? null)) return false;
  return true;
}

/** The server's queue and stash differ from ours by one finished weave, or by a Grand Magick that spent its pieces. */
function explained(q: readonly Element[], st: readonly StashSlot[], sq: readonly Element[], sst: readonly StashSlot[]): boolean {
  const w = weaveTarget(q);
  if (w && sameStash(st, sst) && sameQueue(applyWeave(q, w), sq)) return true;
  const g = matchGrand(q, st);
  if (g && sq.length === 0) return st.every((s, i) => (g.slots.includes(i) ? sst[i] === null : sst[i] === s));
  return false;
}
